import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../../features/setting/setting.slice.ts'
import { getEchartsTheme } from '../../../../../utils/helpers.ts'
import { useGetDeploymentDateRangeQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import dayjs from 'dayjs'
import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import ReactECharts, { EChartsOption } from 'echarts-for-react'
import { DefaultLabelFormatterCallbackParams } from 'echarts'
import { TopLevelFormatterParams } from 'echarts/types/dist/shared'

interface DeploymentDateRangeChartProps {
  bookId: string
}

export const DeploymentDateRangeChart: React.FC<
  DeploymentDateRangeChartProps
> = ({ bookId }) => {
  const { t } = useTranslation()
  const dashboardInput = useAppSelector(selectDashboardInput)
  const themeStyle = useAppSelector(selectThemeStyle)
  const colorTheme = useAppSelector(selectTheme)
  const echartsTheme = getEchartsTheme(colorTheme)
  const { data } = useGetDeploymentDateRangeQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  if (!data) return <></>

  const cash = 1 - data.data.deployment
  const chartOption: EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: TopLevelFormatterParams) => {
        const input = params as DefaultLabelFormatterCallbackParams
        return `${input.name}: ${input.percent?.toFixed(0)}%`
      },
    },
    legend: {
      orient: 'vertical',
      left: 'center',
      bottom: 'bottom',
      textStyle: {
        color: themeStyle.sumText,
      },
    },
    graphic: {
      type: 'text',
      left: 'center',
      top: 'center',
      style: {
        text: `${t('dashboard.deployment')}: ${(data.data.deployment * 100).toFixed(0)}%\n${t('dashboard.cash')}: ${(
          cash * 100
        ).toFixed(0)}%`,
        fill: themeStyle.sumText,
        fontSize: 16,
      },
    },
    series: [
      {
        name: '',
        type: 'pie',
        radius: ['60%', '70%'],
        data:
          data.data.deployment < 0
            ? [
                {
                  value: cash,
                  name: t('dashboard.cash'),
                },
              ]
            : [
                {
                  value: data.data.deployment,
                  name: t('dashboard.deployment'),
                },
                {
                  value: cash,
                  name: t('dashboard.cash'),
                },
              ],
        label: {
          show: false,
        },
      },
    ],
  }

  return (
    <DashboardChartBox title={t('dashboard.average_deployment')}>
      <ReactECharts option={chartOption} theme={echartsTheme.themeName} />
    </DashboardChartBox>
  )
}
